import { useMemo } from 'react';
import { resolveHoldingQuantity, toFiniteNumber } from '../dashboardUtils';
import { combineReadinessSources } from '../../../utils/positionReadiness';
import { getCanonicalRiskIdentity } from '../../../utils/riskOverviewIdentityJoin';

function isBlankDisplayValue(value) {
    if (value == null) return true;
    if (typeof value === 'string') return value.trim() === '';
    if (typeof value === 'number') return Number.isNaN(value);
    return false;
}

function firstNonBlank(...values) {
    for (const value of values) {
        if (!isBlankDisplayValue(value)) return value;
    }
    return null;
}

const MARKET_DATA_CARD_FIELDS = Object.freeze([
    'adtv',
    'adtv_updated_at',
    'article_as_of',
    'article_count',
    'beta',
    'beta_updated_at',
    'fx_rate',
    'fx_source',
    'fx_timestamp',
    'market_status',
    'market_status_asof',
    'next_earnings_date',
    'next_earnings_observed_at',
    'price_asof_date',
    'price_source',
    'quote_timestamp',
    'security_updated_at',
    'updated_at',
    'yahoo_cache_last_checked',
    'yf_exchange',
    'yf_exch_disp',
    'yf_longname',
    'yf_shortname',
    'yf_symbol',
]);

// `combinedHoldings` is the actual-portfolio read model: calculation holdings
// enriched with persisted position metadata and, when requested by Optimizer,
// an optional trade-idea overlay. Position Sizing remains a separate dataset.

function mergeMarketDataCardFields(primary, secondary) {
    return Object.fromEntries(MARKET_DATA_CARD_FIELDS.map((field) => [
        field,
        firstNonBlank(primary?.[field], secondary?.[field]),
    ]));
}

export function combineHoldings({ holdings, positions, actualPortTradeIdeas }) {
    const positionsBySecurity = new Map();
    for (const position of positions || []) {
        const key = getCanonicalRiskIdentity(position);
        if (key) positionsBySecurity.set(key, position);
    }

    const tradeIdeasBySecurity = new Map();
    for (const tradeIdea of actualPortTradeIdeas || []) {
        const key = getCanonicalRiskIdentity(tradeIdea);
        if (key) tradeIdeasBySecurity.set(key, tradeIdea);
    }

    const merged = (holdings || []).map((h) => {
        const key = getCanonicalRiskIdentity(h);
        const pos = key ? positionsBySecurity.get(key) : undefined;
        const idea = key ? tradeIdeasBySecurity.get(key) : undefined;
        const mergedPrice = toFiniteNumber(pos?.price ?? h?.price);
        const marketDataStatus = pos?.market_data_status ?? h?.market_data_status ?? null;
        const availabilityStatus = (
            pos?.actual_portfolio_market_data_status
            ?? marketDataStatus?.actual_portfolio_status
            ?? (mergedPrice === null ? 'missing_price' : null)
        );

        // Reconcile calc-time and boundary-time readiness. When they
        // disagree we take the MORE CONSERVATIVE view: a "ready" from the
        // calc pipeline must never overwrite a "pending"/"failed" reported
        // by /fetch_positions (which sees the MSI app_readiness_status
        // directly). Reasons from both sources are merged so the tooltip
        // shows every applicable cause.
        const combinedReadiness = combineReadinessSources(
            h?.readiness_status || h?.readiness_reasons || h?.included_in_risk != null ? {
                readiness_status: h?.readiness_status,
                included_in_risk: h?.included_in_risk,
                readiness_reasons: h?.readiness_reasons,
            } : null,
            pos?.readiness_status || pos?.readiness_reasons || pos?.included_in_risk != null ? {
                readiness_status: pos?.readiness_status,
                included_in_risk: pos?.included_in_risk,
                readiness_reasons: pos?.readiness_reasons,
            } : null,
        );
        const readinessReasons = combinedReadiness.readiness_reasons;
        const readinessStatus = combinedReadiness.readiness_status;
        const includedInRisk = combinedReadiness.included_in_risk;

        // Display fields (ticker/local_price/local_currency/inbound_id_*
        // and names): prefer the calc row when non-blank, otherwise
        // backfill from the matching /fetch_positions row. This matters
        // most for pending rows, whose calc-side enrichment can be blank
        // while /fetch_positions still has those fields via the
        // portfolio-readiness join.
        return {
            ...h,
            ...mergeMarketDataCardFields(h, pos),
            quantity: resolveHoldingQuantity(h, pos),
            price: mergedPrice,
            ticker: firstNonBlank(h.ticker, pos?.ticker),
            local_price: firstNonBlank(h.local_price, pos?.local_price),
            local_currency: firstNonBlank(h.local_currency, pos?.local_currency),
            inbound_id_type: firstNonBlank(h.inbound_id_type, pos?.inbound_id_type),
            inbound_id_value: firstNonBlank(h.inbound_id_value, pos?.inbound_id_value),
            company_name: firstNonBlank(h.company_name, pos?.company_name),
            uploaded_ticker: firstNonBlank(h.uploaded_ticker, pos?.uploaded_ticker),
            yf_longname: firstNonBlank(h.yf_longname, pos?.yf_longname),
            yf_shortname: firstNonBlank(h.yf_shortname, pos?.yf_shortname),
            msi_listing_label: firstNonBlank(
                h.msi_listing_label,
                pos?.msi_listing_label,
            ),
            cognive_id: pos?.cognive_id ?? h.cognive_id ?? null,
            country: pos ? pos.country ?? null : h.country ?? null,
            display_sedol: pos?.display_sedol ?? pos?.sedol ?? h.display_sedol ?? h.sedol ?? null,
            sedol: pos?.display_sedol ?? pos?.sedol ?? h.display_sedol ?? h.sedol ?? null,
            trade_idea: idea?.trade_idea ?? null,
            exp_return: idea?.exp_return ?? null,
            market_data_status: marketDataStatus,
            actual_portfolio_market_data_status: availabilityStatus,
            _availabilityStatus: availabilityStatus === 'ready' ? null : availabilityStatus,
            readiness_status: readinessStatus,
            included_in_risk: includedInRisk,
            readiness_reasons: readinessReasons,
        };
    });

    const existingKeys = new Set();
    merged.forEach((row) => {
        const key = getCanonicalRiskIdentity(row);
        if (key) existingKeys.add(key);
    });

    (positions || []).forEach((pos) => {
        const key = getCanonicalRiskIdentity(pos);
        if (!key || existingKeys.has(key)) return;

        const idea = tradeIdeasBySecurity.get(key);
        const availabilityStatus = pos?.actual_portfolio_market_data_status
            ?? (toFiniteNumber(pos?.price) === null ? 'missing_price' : null);
        // Position-only rows come from /fetch_positions because the risk
        // calculation omitted them. Missing readiness remains pending.
        const positionOnlyReadiness = combineReadinessSources(
            pos?.readiness_status || pos?.readiness_reasons || pos?.included_in_risk != null ? {
                readiness_status: pos?.readiness_status,
                included_in_risk: pos?.included_in_risk,
                readiness_reasons: pos?.readiness_reasons,
            } : null,
        );
        merged.push({
            id: key,
            ...mergeMarketDataCardFields(null, pos),
            cognive_id: pos?.cognive_id ?? null,
            country: pos?.country ?? null,
            ticker: pos?.ticker ?? null,
            display_sedol: pos?.display_sedol ?? pos?.sedol ?? null,
            sedol: pos?.display_sedol ?? pos?.sedol ?? null,
            quantity: resolveHoldingQuantity({}, pos),
            nmv: null,
            price: pos?.price ?? null,
            local_price: pos?.local_price ?? null,
            local_currency: pos?.local_currency ?? null,
            inbound_id_type: pos?.inbound_id_type ?? null,
            inbound_id_value: pos?.inbound_id_value ?? null,
            company_name: pos?.company_name ?? null,
            uploaded_ticker: pos?.uploaded_ticker ?? null,
            yf_longname: pos?.yf_longname ?? null,
            yf_shortname: pos?.yf_shortname ?? null,
            msi_listing_label: pos?.msi_listing_label ?? null,
            idioVol: null,
            idioRiskDollar: null,
            idioVarPct: null,
            factorVarPct: null,
            factorVol: null,
            factorRiskDollar: null,
            mcfr_bp: null,
            mctr_bp: null,
            mcsr_bp: null,
            trade_idea: idea?.trade_idea ?? null,
            exp_return: idea?.exp_return ?? null,
            market_data_status: pos?.market_data_status ?? null,
            actual_portfolio_market_data_status: availabilityStatus,
            _availabilityStatus: availabilityStatus,
            readiness_status: positionOnlyReadiness.readiness_status,
            included_in_risk: positionOnlyReadiness.included_in_risk,
            readiness_reasons: positionOnlyReadiness.readiness_reasons,
        });
        existingKeys.add(key);
    });

    return merged;
}

export default function useCombinedHoldings({ holdings, positions, actualPortTradeIdeas }) {
    return useMemo(() => combineHoldings({ holdings, positions, actualPortTradeIdeas }),
        [actualPortTradeIdeas, holdings, positions]);
}
